import React, { useState, useRef, useEffect } from 'react';
import { Box, TextField, IconButton, Typography, Chip, Tooltip } from '@mui/material';
import { Edit as EditIcon, Warning as WarningIcon } from '@mui/icons-material';

interface CaptionEditorProps {
  caption: string;
  hasCaption: boolean;
  onSave: (caption: string) => void;
  onCancel: () => void;
  isEditing: boolean;
  onEditClick: () => void;
  onMoveToNext?: () => void;
  onMoveToPrevious?: () => void;
  isReadOnly?: boolean;
}

const CaptionEditor: React.FC<CaptionEditorProps> = ({
  caption,
  hasCaption,
  onSave,
  onCancel,
  isEditing,
  onEditClick,
  onMoveToNext,
  onMoveToPrevious,
  isReadOnly = false,
}) => {
  const [editValue, setEditValue] = useState(caption);
  const inputRef = useRef<HTMLInputElement>(null);

  // Update edit value when caption prop changes
  useEffect(() => {
    setEditValue(caption);
  }, [caption]);

  // Focus input when editing starts
  useEffect(() => {
    if (isEditing) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [isEditing]);

  const handleSave = () => {
    const trimmedCaption = editValue.trim();
    onSave(trimmedCaption);
  };

  const handleCancel = () => {
    setEditValue(caption);
    onCancel();
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleSave();
      if (onMoveToNext) {
        onMoveToNext();
      }
    } else if (event.key === 'Tab') {
      event.preventDefault();
      handleSave();
      if (event.shiftKey) {
        // Shift+Tab: move to previous photo
        if (onMoveToPrevious) {
          onMoveToPrevious();
        }
      } else {
        // Tab: move to next photo
        if (onMoveToNext) {
          onMoveToNext();
        }
      }
    } else if (event.key === 'Escape') {
      handleCancel();
    }
  };

  const handleBlur = () => {
    handleSave();
  };

  if (isEditing) {
    return (
      <Box sx={styles.editingContainer}>
        <TextField
          inputRef={inputRef}
          fullWidth
          multiline
          rows={2}
          value={editValue}
          onChange={e => setEditValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          placeholder='Enter photo caption...'
          size='small'
        />
      </Box>
    );
  }

  const handleDisplayKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onEditClick();
    }
  };

  if (isReadOnly) {
    return (
      <Box sx={styles.displayContainer}>
        <Typography variant='body2' color='text.secondary' sx={styles.captionText}>
          {hasCaption ? caption : ''}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={styles.displayContainer}
      onClick={onEditClick}
      onKeyDown={handleDisplayKeyDown}
      role='button'
      tabIndex={0}
      aria-label='Edit caption'
    >
      <Typography variant='body2' color='text.secondary' sx={styles.captionText}>
        {hasCaption ? (
          caption
        ) : (
          <Tooltip title='Missing caption'>
            <Chip icon={<WarningIcon />} label='No caption' size='small' color='warning' sx={styles.warningChip} onClick={onEditClick} />
          </Tooltip>
        )}
      </Typography>
      <Tooltip title='Edit caption'>
        <IconButton size='small' onClick={onEditClick} sx={styles.editButton}>
          <EditIcon fontSize='small' />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

const styles = {
  editingContainer: {
    display: 'flex',
    gap: 1,
    alignItems: 'flex-start',
  },
  displayContainer: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 1,
    cursor: 'text',
  },
  captionText: {
    minHeight: '2.5rem',
    display: 'flex',
    alignItems: 'center',
    flex: 1,
  },
  warningChip: {
    backgroundColor: 'warning.light',
    color: 'text.primary',
  },
  editButton: {
    color: 'text.secondary',
    '&:hover': {
      color: 'primary.main',
    },
  },
};

export default CaptionEditor;
